<template>
  <div>
    <!-- Internal analytics dashboard (feature flag: kp_3080_internal_analytics) -->
    <div
      v-if="hasInternalAnalytics"
      class="analytics-container"
    >
      <!-- Page header -->
      <div class="analytics-header mb-2">
        <div>
          <div class="d-flex align-items-center">
            <feather-icon
              icon="PieChartIcon"
              size="20"
              class="mr-75 text-primary"
            />
            <h4 class="mb-0 analytics-title">
              Analytics Dashboard
            </h4>
          </div>
          <p class="analytics-subtitle mb-0">
            Track your brand's order and sales performance
          </p>
        </div>
        <button
          v-b-tooltip.hover.left="'Download as PDF'"
          type="button"
          class="pdf-btn"
          :disabled="isLoading"
          @click="downloadAsPdf"
        >
          <feather-icon
            icon="DownloadCloudIcon"
            size="18"
          />
        </button>
      </div>

      <!-- Filters -->
      <analytics-filters
        :retailers="availableRetailers"
        :collections="availableCollections"
        :products="availableProducts"
        :genders="availableGenders"
        :departments="availableDepartments"
        :categories="availableCategories"
        :subcategories="availableSubcategories"
        :colors="availableColors"
        @filter-change="onFilterChange"
      />

      <!-- Loading state: KPIs + chart + table placeholders to avoid CLS -->
      <div
        v-if="isLoading"
        class="loading-state"
      >
        <!-- KPI row -->
        <div class="skeleton-row skeleton-row--kpi">
          <div
            v-for="i in 4"
            :key="`kpi-${i}`"
            class="skeleton-card skeleton-card--kpi"
          />
        </div>
        <!-- Chart -->
        <div class="skeleton-card skeleton-card--chart" />
        <!-- Table row -->
        <div class="skeleton-row skeleton-row--tables">
          <div class="skeleton-card skeleton-card--table-lg" />
          <div class="skeleton-card skeleton-card--table-sm" />
        </div>
      </div>

      <!-- Dashboard content -->
      <template v-else>
        <analytics-k-p-i-cards
          :kpis="dashboardData.kpis"
        />

        <analytics-orders-chart
          :orders-over-time="dashboardData.ordersOverTime"
        />

        <analytics-orders-table
          :orders="dashboardData.orders"
        />

        <analytics-popular-products
          :popular-products="dashboardData.popularProducts"
        />

        <analytics-sales-by-retailer
          :sales-by-retailer="dashboardData.salesByRetailer"
        />
      </template>
    </div>

    <!-- Makeke iframe fallback -->
    <div v-else>
      <div v-if="isLoadedAnalyticsUrl">
        <iframe
          v-resize="{ log: false, defaultHeight: 600 }"
          class="border-0"
          title="Makeke Dashboard"
          width="100%"
          height="600"
          :src="makekeDashboardUrl"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
      <template v-else>
        <b-card class="text-center p-4">
          <b-spinner />
        </b-card>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { BCard, BSpinner, VBTooltip } from 'bootstrap-vue'
import { apiToastError } from '@/@core/utils/toast'
import { getToken } from '@/store/jwt.service'
import analytics from '@/@core/utils/analytics'
import constants, { FLAG_CONSTANTS } from '@/constants'
import { useAnalytics } from '@/composables/useAnalytics'
import AnalyticsFilters from './components/AnalyticsFilters.vue'
import AnalyticsKPICards from './components/AnalyticsKPICards.vue'
import AnalyticsOrdersChart from './components/AnalyticsOrdersChart.vue'
import AnalyticsOrdersTable from './components/AnalyticsOrdersTable.vue'
import AnalyticsPopularProducts from './components/AnalyticsPopularProducts.vue'
import AnalyticsSalesByRetailer from './components/AnalyticsSalesByRetailer.vue'

const LOCAL_ORDERS_SERVICE = process.env.VUE_APP_ORDERS_SERVICE_URL
const isLocalDev = !!LOCAL_ORDERS_SERVICE

const uniqueBy = (arr, key) => {
  const seen = new Set()
  return arr.filter(item => {
    if (seen.has(item[key])) return false
    seen.add(item[key])
    return true
  })
}

const emptyDashboard = () => ({
  kpis: {
    orders: 0, productsSold: 0, unitsSold: 0, totalSales: 0,
  },
  ordersOverTime: [],
  orders: [],
  popularProducts: [],
  salesByRetailer: [],
})

export default {
  name: 'Analytics',
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BCard,
    BSpinner,
    AnalyticsFilters,
    AnalyticsKPICards,
    AnalyticsOrdersChart,
    AnalyticsOrdersTable,
    AnalyticsPopularProducts,
    AnalyticsSalesByRetailer,
  },
  setup() {
    const { makekeDashboardUrl, isLoadedAnalyticsUrl, getAnalyticsUrl } = useAnalytics()
    return { makekeDashboardUrl, isLoadedAnalyticsUrl, getAnalyticsUrl }
  },
  data() {
    return {
      isLoading: true,
      filters: {},
      dashboardData: emptyDashboard(),
      availableRetailers: [],
      availableCollections: [],
      availableProducts: [],
      availableGenders: [],
      availableDepartments: [],
      availableCategories: [],
      availableSubcategories: [],
      availableColors: [],
      fetchRequestId: 0,
    }
  },
  computed: {
    hasInternalAnalytics() {
      return this.$store.getters['flags/hasFeature'](FLAG_CONSTANTS.KP_3080_INTERNAL_ANALYTICS)
    },
  },
  watch: {
    hasInternalAnalytics: {
      immediate: false,
      handler(val) {
        if (!val) this.getAnalyticsUrl()
      },
    },
  },
  created() {
    analytics.track(constants.TRACKS.PAGES.BRAND_CLICKS_ANALYTICS)
    analytics.track(constants.TRACKS.PAGES.BRAND_OPENS_ANALYTICS_PAGE)
    if (!this.hasInternalAnalytics) {
      this.getAnalyticsUrl()
    }
    // fetchDashboard is triggered by AnalyticsFilters emitting on mounted
  },
  destroyed() {
    this._isDestroyed = true
    analytics.track(constants.TRACKS.PAGES.BRAND_EXITS_ANALYTICS_PAGE)
  },
  methods: {
    onFilterChange(newFilters) {
      this.filters = { ...newFilters }
      this.fetchDashboard()
    },
    appendUnique(dest, newItems, keyFn) {
      const existing = new Set(this[dest].map(keyFn))
      const added = newItems.filter(item => !existing.has(keyFn(item)))
      if (added.length) this[dest] = [...this[dest], ...added]
    },
    mergeRetailersAndCollections(data) {
      const retailers = uniqueBy(data.salesByRetailer || [], 'retailerId')
        .map(r => ({ id: r.retailerId, name: r.retailerName }))
      this.appendUnique('availableRetailers', retailers, r => r.id)

      const collections = uniqueBy(data.orders || [], 'collectionId')
        .filter(o => o.collectionId)
        .map(o => ({ id: o.collectionId, name: o.collectionName }))
      this.appendUnique('availableCollections', collections, c => c.id)
    },
    mergeProducts(data) {
      const fromOptions = (data.filterOptions?.products || []).filter(p => p.id)
      const products = fromOptions.length
        ? fromOptions
        : (data.popularProducts || [])
            .filter(p => p.productId)
            .map(p => ({ id: p.productId, name: p.productName || p.styleCode || p.productId }))
      this.appendUnique('availableProducts', products, p => p.id)
    },
    mergeDimensions(filterOptions) {
      const map = [
        { src: filterOptions.genders, dest: 'availableGenders' },
        { src: filterOptions.departments, dest: 'availableDepartments' },
        { src: filterOptions.categories, dest: 'availableCategories' },
        { src: filterOptions.subcategories, dest: 'availableSubcategories' },
        { src: filterOptions.colors, dest: 'availableColors' },
      ]
      map.forEach(({ src, dest }) => {
        const vals = (src || []).filter(Boolean)
        if (!vals.length) return
        const existing = new Set(this[dest])
        const added = vals.filter(v => !existing.has(v))
        if (added.length) this[dest] = [...this[dest], ...added].sort()
      })
    },
    mergeFilterOptions(data) {
      this.mergeRetailersAndCollections(data)
      this.mergeProducts(data)
      this.mergeDimensions(data.filterOptions || {})
    },
    fetchApiData() {
      if (isLocalDev) return this.fetchLocalDev()
      return this.$kpRequest({ ...this.$kpEndpoint.integrations.brandAnalytics, params: this.filters })
    },
    parseDashboardResponse(response) {
      return response?.data?.data || emptyDashboard()
    },
    fetchLocalDev() {
      const profile = this.$store.getters.profile
      return axios.get(`${LOCAL_ORDERS_SERVICE}/analytics/brand`, {
        params: this.filters,
        headers: {
          Authorization: `Bearer ${getToken()}`,
          userId: profile?.id,
          role: profile?.role,
          entitytype: this.$store.state.auth.entityType,
          entityid: this.$store.state.auth.currentEntityId,
        },
      })
    },
    async fetchDashboard() {
      this.isLoading = true
      this.fetchRequestId += 1
      const fetchId = this.fetchRequestId
      try {
        const response = await this.fetchApiData()
        if (fetchId !== this.fetchRequestId) return
        const data = this.parseDashboardResponse(response)
        this.dashboardData = data
        this.mergeFilterOptions(data)
      } catch (error) {
        if (fetchId !== this.fetchRequestId) return
        apiToastError(error)
        this.dashboardData = emptyDashboard()
      } finally {
        if (fetchId === this.fetchRequestId && !this._isDestroyed) this.isLoading = false
      }
    },
    downloadAsPdf() {
      // Inject page-break divs before each table card so every section title
      // always starts at the top of a new page regardless of how long the table is.
      const cards = Array.from(document.querySelectorAll('.analytics-table-card'))
      const injected = []
      cards.forEach(card => {
        const br = document.createElement('div')
        br.className = 'print-page-break'
        card.parentNode.insertBefore(br, card)
        injected.push(br)
      })

      window.print()

      // Clean up after the print dialog closes
      injected.forEach(el => el.remove())
    },
  },
}
</script>

<style lang="scss" scoped>
.analytics-container {
  padding: 4px 0;
}

.analytics-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  .analytics-title {
    font-weight: 700;
    color: #1e3a8a;
    font-size: 1.25rem;
  }

  .analytics-subtitle {
    font-size: 0.8rem;
    color: #9ca3af;
    margin-top: 2px;
    margin-left: 31px;
  }
}

.pdf-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  flex-shrink: 0;
  margin-top: 2px;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    color: #1e40af;
    border-color: #93c5fd;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* --- Skeleton loading --------------------------------------------------- */
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-card {
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f5ff 25%, #e0ebff 50%, #f0f5ff 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease infinite;
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.skeleton-row {
  display: flex;
  gap: 16px;

  &--kpi {
    .skeleton-card--kpi {
      flex: 1;
      height: 100px;
    }
  }

  &--tables {
    .skeleton-card--table-lg { flex: 2; height: 260px; }
    .skeleton-card--table-sm { flex: 1; height: 260px; }
  }
}

.skeleton-card--chart { height: 280px; width: 100%; }

@media (max-width: 768px) {
  .skeleton-row--kpi { flex-wrap: wrap; }
  .skeleton-card--kpi { flex: 1 1 calc(50% - 8px); }
  .skeleton-row--tables { flex-direction: column; }
  .skeleton-card--table-lg,
  .skeleton-card--table-sm { flex: none; width: 100%; }
}
</style>

<style lang="scss">
@media print {
  /*
   * Visibility-isolation: hide the entire page then reveal only the analytics
   * container. visibility:hidden on a parent can be overridden by
   * visibility:visible on a child — unlike display:none.
   */
  body * { visibility: hidden !important; }

  .analytics-container,
  .analytics-container * { visibility: visible !important; }

  /* Position container at the top-left of the print sheet */
  .analytics-container {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    padding: 20pt 24pt !important;
    background: #fff !important;
  }

  /* Hide interactive-only elements inside the container */
  .pdf-btn, .pdf-btn *,
  .dm, .dm *,
  .atf-reset-btn, .atf-reset-btn *,
  .analytics-header, .analytics-header *,
  .tooltip, .b-tooltip { visibility: hidden !important; }

  /* "Sales Dashboard" title injected above the filters */
  .analytics-container::before {
    content: 'Sales Dashboard';
    visibility: visible !important;
    display: block;
    font-size: 22pt;
    font-weight: 700;
    color: #000;
    margin-bottom: 12pt;
  }

  /* JS-injected page-break divs */
  .print-page-break {
    visibility: visible !important;
    display: block !important;
    page-break-after: always;
    break-after: page;
    height: 0;
  }

  /* Keep section title glued to first table rows */
  .card-header { break-after: avoid; page-break-after: avoid; }

  * {
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
